import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { rooms, restaurants, facilities, articles, categoryTabs, transportOptions, aboutInfo } from '@/mocks/hotelData';

const categoryMeta: Record<string, { title: string; desc: string }> = {
  rooms: { title: '房型选择', desc: '探索新葡京酒店各类豪华客房与套房，从舒适大床房到极致总统套房，满足您不同的住宿需求。' },
  dining: { title: '餐厅美食', desc: '新葡京酒店汇聚澳门最多米其林星级餐厅，从天巢法国餐厅到8餐厅，为您呈现顶级美食盛宴。' },
  facilities: { title: '设施娱乐', desc: '水疗中心、恒温泳池、健身中心和娱乐场，新葡京酒店为您提供全方位的奢华设施与服务。' },
  transport: { title: '周边交通', desc: '了解从澳门各口岸和机场前往新葡京酒店的交通方式，包括免费穿梭巴士时刻表和路线指引。' },
  articles: { title: '入住攻略', desc: '澳门旅游攻略与入住指南，从预订流程到周边景点，助您轻松规划完美澳门之旅。' },
};

const breadcrumbMap: Record<string, { label: string; path: string }> = {
  rooms: { label: '房型选择', path: '/category/rooms' },
  dining: { label: '餐厅美食', path: '/category/dining' },
  facilities: { label: '设施娱乐', path: '/category/facilities' },
  transport: { label: '周边交通', path: '/category/transport' },
  articles: { label: '入住攻略', path: '/category/articles' },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const activeTab = slug || 'rooms';
  const meta = categoryMeta[activeTab] || categoryMeta.rooms;

  const handleTabChange = (tabId: string) => {
    navigate(`/category/${tabId}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'rooms':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {rooms.map((room) => (
              <article
                key={room.id}
                className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all duration-300"
              >
                <div className="relative h-52 md:h-60 overflow-hidden">
                  <img
                    src={room.imageUrl}
                    alt={room.name}
                    title={`${room.name} - 新葡京酒店`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 bg-background-50/90 text-foreground-700 text-xs font-medium rounded-full">
                      {room.category}
                    </span>
                  </div>
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1">{room.name}</h3>
                  <p className="text-xs text-foreground-500 mb-3">{room.nameEn}</p>
                  <div className="flex items-center gap-4 text-xs text-foreground-600 mb-3">
                    <span className="flex items-center gap-1"><i className="ri-aspect-ratio-line text-sm"></i> {room.size}</span>
                    <span className="flex items-center gap-1"><i className="ri-hotel-bed-line text-sm"></i> {room.bedType}</span>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{room.description}</p>
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1.5">
                      {room.features.map((f) => (
                        <span key={f} className="px-2 py-0.5 bg-background-100 text-foreground-600 text-xs rounded-md">{f}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-end justify-between pt-3 border-t border-background-200/70">
                    <span className="font-heading text-lg font-bold text-primary-600">{room.price}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        );
      case 'dining':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {restaurants.map((r) => (
              <article
                key={r.id}
                className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-accent-300/50 transition-all duration-300"
              >
                <div className="relative h-48 md:h-56 overflow-hidden">
                  <img
                    src={r.imageUrl}
                    alt={r.name}
                    title={`${r.name} - 新葡京酒店`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full">{r.rating}</span>
                  </div>
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1">{r.name}</h3>
                  <p className="text-xs text-foreground-500 mb-2">{r.nameEn} · {r.cuisine}</p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{r.description}</p>
                  <div className="flex flex-col gap-1.5 text-xs text-foreground-500">
                    <span className="flex items-center gap-1"><i className="ri-time-line text-sm"></i> {r.hours}</span>
                    <span className="flex items-center gap-1"><i className="ri-map-pin-line text-sm"></i> {r.location}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        );
      case 'facilities':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {facilities.map((f) => (
              <div
                key={f.id}
                className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8 hover:border-secondary-300/50 transition-all duration-300"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-5">
                  <i className={`${f.icon} text-2xl`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">{f.name}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        );
      case 'transport':
        return (
          <div className="space-y-4">
            {transportOptions.map((t) => (
              <div
                key={t.id}
                className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 shrink-0">
                  <i className="ri-bus-line text-xl"></i>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading text-base font-semibold text-foreground-900">{t.from}</h3>
                    <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">{t.method}</span>
                  </div>
                  <p className="text-sm text-foreground-500 mb-2">{t.description}</p>
                  <div className="flex items-center gap-4 text-xs text-foreground-600">
                    <span className="flex items-center gap-1"><i className="ri-time-line text-sm"></i> {t.duration}</span>
                    <span className="flex items-center gap-1"><i className="ri-money-cny-circle-line text-sm"></i> {t.cost}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      case 'articles':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {articles.map((article) => (
              <article
                key={article.id}
                className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                onClick={() => navigate(`/article/${article.slug}`)}
              >
                <div className="relative h-44 md:h-52 overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    title={`${article.title} - 新葡京酒店攻略`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 bg-background-50/90 text-foreground-700 text-xs font-medium rounded-full">{article.category}</span>
                  </div>
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2">
                    <span>{article.date}</span>
                    <span>·</span>
                    <span>{article.author}</span>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">{article.excerpt}</p>
                  <div className="flex items-center gap-2 mt-4 flex-wrap">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-background-100 text-foreground-500 text-xs rounded-md">{tag}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="WebPage" data={{
        name: `${meta.title} - ${aboutInfo.brandName}`,
        description: meta.desc,
        url: `https://www.grandlisboa.com/category/${activeTab}`,
        isPartOf: { '@type': 'WebSite', name: aboutInfo.brandName, url: 'https://www.grandlisboa.com' },
      }} />

      <header>
        <Navbar />
      </header>

      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="pt-6">
              <Breadcrumb items={[
                { label: '首页', path: '/' },
                { label: breadcrumbMap[activeTab]?.label || '分类' },
              ]} />
            </div>
            <div className="pb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">{meta.title}</h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl">{meta.desc}</p>
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 md:top-18 z-30">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-1 py-3 overflow-x-auto flex-nowrap">
              {categoryTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === tab.id
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-10 md:py-14">
          {renderContent()}
        </div>
      </main>

      <Footer />
    </>
  );
}